import moment from 'moment';
import { APIKeyDetails } from '../constants/types';

export type SortKey =
  | 'name'
  | 'active_from'
  | 'expires_at'
  | 'updated'
  | 'last_used_on';

export interface ApiKeyFilters {
  maskedKey?: string;
  name?: string;
  activeFromAfter?: moment.Moment;
  expireAtBefore?: moment.Moment;
  updatedRange?: [moment.Moment | null, moment.Moment | null];
  status?: APIKeyDetails['status'][];
}

export type LegacyAPIKeyDetails = APIKeyDetails & {
  keyName?: string;
  keyDescription?: string;
  name1?: string;
  name2?: string;
  status1?: string;
};

export const DEFAULT_SORT: { key: SortKey; order: 'asc' | 'desc' } = {
  key: 'expires_at',
  order: 'asc',
};

export const DEFAULT_STATUS_FILTER: APIKeyDetails['status'][] = [
  'Active',
  'ActiveInFuture',
];

export const STATUS_OPTIONS: {
  label: string;
  value: APIKeyDetails['status'];
}[] = [
  { label: 'ApiKeys.status.active', value: 'Active' },
  { label: 'ApiKeys.status.activeInFuture', value: 'ActiveInFuture' },
  { label: 'ApiKeys.status.expired', value: 'Expired' },
  { label: 'ApiKeys.status.revoked', value: 'Revoked' },
];

/**
 * Compact masked key — replaces the long `xxx****xxx` with `xxx…xxx`.
 */
export const compactMaskedKey = (masked: string | undefined): string => {
  if (!masked) return '';
  const m = masked.match(/^(.{3}).*?(.{3})$/);
  return m ? `${m[1]}…${m[2]}` : masked;
};

export const truncateText = (
  value: string | undefined,
  maxLength = 20,
): string => {
  if (!value) return '';
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
};

export const normalizeStatus = (
  rawStatus: string | undefined,
): APIKeyDetails['status'] => {
  if (rawStatus === 'ActiveInFuture' || rawStatus === 'Active in future') {
    return 'ActiveInFuture';
  }
  if (rawStatus === 'Expired') return 'Expired';
  if (rawStatus === 'Revoked') return 'Revoked';
  return 'Active';
};

export const normalizeApiKey = (record: LegacyAPIKeyDetails): APIKeyDetails => {
  const rawStatus = record.status || record.status1;
  return {
    ...record,
    name: record.name || record.keyName || record.name1 || '',
    description:
      record.description || record.keyDescription || record.name2 || '',
    status: normalizeStatus(rawStatus),
  };
};

export const getExpiringKeyMessage = (
  instance: APIKeyDetails,
): { messageKey: string; days: number } | null => {
  if (instance.status !== 'Active' && instance.status !== 'ActiveInFuture') {
    return null;
  }
  const today = moment().endOf('day').valueOf();
  const expiryDate = moment(instance.expireAt).valueOf();
  const activeDays = Math.round(moment.duration(expiryDate - today).asDays());
  const keyDuration = moment(instance.expireAt).diff(
    moment(instance.activeFrom),
    'days',
  );

  if (activeDays <= 1) {
    return moment(instance.expireAt).isSame(moment(), 'day')
      ? { messageKey: 'ApiKeys.expiry.today', days: activeDays }
      : { messageKey: 'ApiKeys.expiry.tomorrow', days: activeDays };
  }
  if (
    (keyDuration <= 5 && activeDays <= 2) ||
    (keyDuration > 5 && keyDuration <= 7 && activeDays <= 3) ||
    (keyDuration > 7 && keyDuration <= 31 && activeDays <= 7) ||
    (keyDuration > 31 && activeDays <= 30)
  ) {
    return { messageKey: 'ApiKeys.expiry.inDays', days: activeDays };
  }
  return null;
};

export const getStatusLabelId = (status: APIKeyDetails['status']): string => {
  if (status === 'ActiveInFuture') return 'ApiKeys.status.activeInFuture';
  if (status === 'Expired') return 'ApiKeys.status.expired';
  if (status === 'Revoked') return 'ApiKeys.status.revoked';
  return 'ApiKeys.status.active';
};

// Backend stores the API key as a masked string of the same length as the
// plain key: 3-char prefix + asterisks + 3-char suffix. To support searching
// by the full plain key — which the API never echoes back — we convert the
// user's typed query into the same masked shape before sending it as the
// `maskedKey` substring filter.
//
// - Full-length inputs (≥ 20, the minimum allowed key length): produce
//   `prefix3 + middle* + suffix3` so the pattern equals the stored value.
// - Shorter inputs are treated as a typed prefix. The masked middle is capped
//   at 14 chars (positions 3-16) — the minimum middle length any stored
//   masked can have, since a 20-char key stores 3+14+3 — and any chars typed
//   past position 17 are kept literal as a partial suffix, so the pattern
//   stays a substring of the stored value while still discriminating by the
//   typed trailing characters.
export const toMaskedKeySearch = (keyword: string): string => {
  const regex = /[0-9a-zA-Z-]/g;
  if (keyword.length >= 20) {
    return (
      keyword.slice(0, 3) +
      keyword.slice(3, -3).replace(regex, '*') +
      keyword.slice(-3)
    );
  }
  return (
    keyword.slice(0, 3) +
    keyword.slice(3, 17).replace(regex, '*') +
    keyword.slice(17)
  );
};

export const buildApiKeysQuery = (
  page: number,
  pageSize: number,
  sort: { key: SortKey; order: 'asc' | 'desc' },
  filters: ApiKeyFilters,
): string => {
  const params = new URLSearchParams();
  params.set('_page', String(page));
  params.set('_size', String(pageSize));
  params.set('_sort', `${sort.order === 'desc' ? '-' : ''}${sort.key}`);
  if (filters.name) params.set('name', filters.name);
  if (filters.maskedKey) {
    params.set('maskedKey', toMaskedKeySearch(filters.maskedKey));
  }
  if (filters.activeFromAfter) {
    params.set('activeFromAfter', filters.activeFromAfter.toISOString());
  }
  if (filters.expireAtBefore) {
    params.set('expireAtBefore', filters.expireAtBefore.toISOString());
  }
  if (filters.updatedRange?.[0]) {
    params.set('updatedAfter', filters.updatedRange[0].toISOString());
  }
  if (filters.updatedRange?.[1]) {
    params.set('updatedBefore', filters.updatedRange[1].toISOString());
  }
  if (filters.status && filters.status.length > 0) {
    params.set('status', JSON.stringify(filters.status));
  }
  return `&${params.toString()}`;
};
